import { useIntl } from 'react-intl';
import Select from '@rio-cloud/rio-uikit/Select';
import SelectCustom from '../../../../../selectCustom';
import { AegFormSectionProps } from '../../../../../../models/newAEG';
import { formatPhoneNumber } from '../../../../../../utils/phoneUtils';
import useAegDealerships from '../../useAegDealerships';

const RequesterSection = ({ values, errors, requiredFields, onChange }: AegFormSectionProps) => {
    const intl = useIntl();
    const requiredMessage = intl.formatMessage({ id: 'intl-msg:support.custom.fieldRequired' });
    const selectPlaceholder = intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.placeholder.select' });
    const noItemMessage = intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.select.noItemFound' });
    const { role, dnOptions, regionalOptions, searchDns, getRegionalIdByDn } = useAegDealerships();
    const shouldUseSearchSelect = role === 'TOWER' || role === 'FACTORY';

    const handleDnChange = async (dn: string) => {
        onChange('dn', dn);
        if (!dn) {
            return;
        }

        const regionalId = await getRegionalIdByDn(dn);
        if (regionalId) {
            onChange('regional', regionalId);
        }
    };

    return (
        <section className='border rounded padding-15'>
            <h3 className='display-flex align-items-center gap-x-2 margin-top-0 margin-bottom-15 text-size-16 text-bold'>
                <span className='rounded-circle bg-primary text-color-white text-size-12 width-25 height-25 inline-flex items-center justify-center shrink-0'>
                    2
                </span>
                {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.section.requester' })}
            </h3>
            <div className='grid grid-cols-[minmax(0,1fr)] sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3'>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-responsible' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.responsible' })}{' '}
                        {requiredFields.includes('responsible') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-responsible'
                        type='text'
                        className='form-control'
                        value={values.responsible}
                        onChange={event => onChange('responsible', event.target.value)}
                    />
                    {errors.responsible && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-dn' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.dn' })}{' '}
                        {requiredFields.includes('dn') && <span className='text-color-danger'>*</span>}
                    </label>
                    {shouldUseSearchSelect ? (
                        <SelectCustom
                            value={[values.dn]}
                            onChange={option => handleDnChange(option?.id ?? '')}
                            loadData={searchDns}
                            loadOnFocus
                            openOnFocus={false}
                            initialLimit={10}
                            placeholder={intl.formatMessage({
                                id: 'intl-msg:support.supportCallDnField.searchPlaceholder',
                            })}
                        />
                    ) : (
                        <Select
                            id='aeg-dn'
                            options={dnOptions}
                            value={values.dn ? [values.dn] : []}
                            onChange={option => handleDnChange(option?.id ?? '')}
                            placeholder={selectPlaceholder}
                            noItemMessage={noItemMessage}
                            useFilter
                        />
                    )}
                    {errors.dn && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-regional' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.regional' })}{' '}
                        {requiredFields.includes('regional') && <span className='text-color-danger'>*</span>}
                    </label>
                    <Select
                        id='aeg-regional'
                        options={regionalOptions}
                        value={values.regional ? [values.regional] : []}
                        onChange={option => onChange('regional', option?.id ?? '')}
                        placeholder={selectPlaceholder}
                        noItemMessage={noItemMessage}
                    />
                    {errors.regional && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-responsible-dn' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.responsibleDn' })}{' '}
                        {requiredFields.includes('responsibleDn') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-responsible-dn'
                        type='text'
                        className='form-control'
                        value={values.responsibleDn}
                        onChange={event => onChange('responsibleDn', event.target.value)}
                    />
                    {errors.responsibleDn && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
                <div className='form-group margin-bottom-0'>
                    <label htmlFor='aeg-phone' className='text-size-12 text-medium text-color-dark'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.newRequest.field.phone' })}{' '}
                        {requiredFields.includes('phone') && <span className='text-color-danger'>*</span>}
                    </label>
                    <input
                        id='aeg-phone'
                        type='text'
                        className='form-control'
                        maxLength={15}
                        value={values.phone}
                        onChange={event => onChange('phone', formatPhoneNumber(event.target.value))}
                    />
                    {errors.phone && <span className='text-size-12 text-color-danger'>{requiredMessage}</span>}
                </div>
            </div>
        </section>
    );
};

export default RequesterSection;
